import { createContext, ReactNode, useContext, useEffect, useState } from 'react';

import { useTranslation } from 'react-i18next';

import { Button } from '@/components/ui/button';

export interface WebhookSaveState {
  isDirty: boolean;
  isSaving: boolean;
  canWrite: boolean;
}

const noop = () => undefined;

const SaveStateContext = createContext<WebhookSaveState | null>(null);
const PublishContext = createContext<(state: WebhookSaveState | null) => void>(noop);

export const WebhookSaveProvider = ({ children }: { children: ReactNode }) => {
  const [state, setState] = useState<WebhookSaveState | null>(null);

  return (
    <PublishContext.Provider value={setState}>
      <SaveStateContext.Provider value={state}>{children}</SaveStateContext.Provider>
    </PublishContext.Provider>
  );
};

export const WebhookSaveButton = () => {
  const { t } = useTranslation();
  const state = useContext(SaveStateContext);

  if (!state?.isDirty) {
    return null;
  }

  return (
    <Button
      type="submit"
      form="webhook-config-form"
      size="sm"
      disabled={!state.canWrite}
      isLoading={state.isSaving}
    >
      {t('common.save')}
    </Button>
  );
};

export const usePublishWebhookSave = ({ isDirty, isSaving, canWrite }: WebhookSaveState) => {
  const publish = useContext(PublishContext);

  useEffect(() => {
    publish({ isDirty, isSaving, canWrite });
  }, [publish, isDirty, isSaving, canWrite]);

  useEffect(() => () => publish(null), [publish]);
};
